import React, { Fragment } from 'react';
import { Link, useActionData, useLoaderData } from '@remix-run/react';
import { json, LinksFunction } from '@remix-run/node';
import {
  CalendarIcon,
  LocationMarkerIcon,
  UsersIcon,
  ChevronRightIcon,
  ChevronLeftIcon } from
'@heroicons/react/solid';
import type {
  LoaderFunction,
  ActionFunction,
  MetaFunction } from
'@remix-run/node';
import {
  unstable_parseMultipartFormData,
  unstable_createMemoryUploadHandler } from
'@remix-run/node';
import {
  getJobListItems,
  getJob,
  applyForJob,
  getJobForView } from
'~/models/job.server';
import { ApplicationForm } from '~/components/ApplicationForm';
import { Transition } from '@headlessui/react';

import { useOptionalUser } from '~/utils';
import { ATS, Job, Prisma } from '@prisma/client';
import { getAccountById } from '~/models/account.server';
import { addEvent } from '~/models/event.server';
import { PageConfiguration } from '~/types';
import { decodeHTML } from 'entities';
import { useSessionRewind } from '~/components/hooks/useSessionRewind';

type LoaderData = {
  job: Awaited<ReturnType<typeof getJobForView>>;
  livePreviewUrl: string;
  includeSessionRewind: boolean;
};

type ActionData = {
  ok?: boolean;
};

export const action: ActionFunction = async ({ request, params }) => {
  // check if file uploaded: data.get('...').size
  const job = await getJob({ id: params.jobId! });

  if (!job) {
    throw new Response('Not found', { status: 404 });
  }

  const uploadHandler = unstable_createMemoryUploadHandler({
    maxPartSize: 500_000
  });

  const data = await unstable_parseMultipartFormData(request, uploadHandler);

  const fromEntries = Object.fromEntries(data);

  const answers = Object.keys(fromEntries).
  filter((key) => key.indexOf('answers.') === 0).
  reduce(
    (acc, key) => ({
      ...acc,
      [key]: fromEntries[key] || null
    }),
    {}
  );

  console.log('a-a--a', Object.keys(fromEntries), answers);

  const result = await applyForJob({ job, data: Object.fromEntries(data) });

  return { ok: true };
};

export const meta: MetaFunction = ({ data }) => {
  const { job, livePreviewUrl } = data as LoaderData;
  const pageConfiguration = job?.account?.careerPage?.
  pageConfiguration as PageConfiguration;

  const meta = pageConfiguration.props.meta;

  const titleArray = [job?.name];

  if (meta?.title) {
    titleArray.push(` - ${meta.title}`);
  }

  const title = titleArray.join('');
  const description = meta?.description ?? '';

  const extraTags = [];

  if (job?.departments && job?.departments.length > 0) {
    extraTags.push({ label: 'Department', data: job?.departments[0] });
  }

  if (job?.locations && job?.locations?.length > 0) {
    extraTags.push({ label: 'Location', data: job?.locations[0] });
  }

  const extra = extraTags.reduce((acc, tag, index) => {
    return {
      ...acc,
      [`twitter:label${index + 1}`]: tag.label,
      [`twitter:data${index + 1}`]: tag.data
    };
  }, {});

  return {
    // title: job?.name,
    title,
    description,
    'og:type': 'website',
    'og:url': `${livePreviewUrl}/jobs/${job?.id}`,
    'og:title': title,
    'og:description': description,
    'og:image':
    meta?.ogImageUrl?.replace(
      'https://s3.us-east-2.amazonaws.com/growhirestaging/',
      'https://d1icxrsvmhiz31.cloudfront.net/'
    ) ?? undefined,
    'twitter:card': meta?.ogImageUrl ? 'summary_large_image' : 'summary',
    'twitter:url': `${livePreviewUrl}/jobs/${job?.id}`,
    'twitter:title': title,
    'twitter:description': description,
    'twitter:image':
    meta?.ogImageUrl?.replace(
      'https://s3.us-east-2.amazonaws.com/growhirestaging/',
      'https://d1icxrsvmhiz31.cloudfront.net/'
    ) ?? undefined,
    ...extra
  };
};

export const loader: LoaderFunction = async ({ request, params, context }) => {
  const id = params.jobId;
  if (!id) {
    throw new Response('Not Found', { status: 404 });
  }

  const job = await getJobForView({ id });

  if (!job) {
    throw new Response('Not Found', { status: 404 });
  }

  const cp = job?.account?.careerPage;

  let livePreviewUrl =
  process.env.NODE_ENV === 'development' ?
  `http://localhost:3000?slug=${cp?.slug}` :
  `https://${cp?.slug}.${process.env.DOMAIN_NAME_FOR_PREVIEW}`;

  if (cp?.customDomain) {
    livePreviewUrl = `https://${cp.customDomain}`;
  }

  // try {
  //   if (job.accountId) {
  //     const account = await getAccountById({ id: job.accountId })
  //     if (account) {
  //       await addEvent({ name: 'page.visit', account, payload: { page: 'job', url: request.url, ip: context.ip } })
  //     }
  //   }
  // } catch (e) {
  //   console.error(e)
  // }

  // @REFACTOR @FIXME
  const jobMeta = job.meta as Prisma.JsonObject | null;

  if (jobMeta?.html) {
    delete jobMeta.html;
  }

  const includeSessionRewind = process.env.NODE_ENV === 'production';

  return json<{
    job: LoaderData['job'];
    livePreviewUrl: string;
    includeSessionRewind: boolean;
  }>({ job, livePreviewUrl, includeSessionRewind });
};

export const links: LinksFunction = () => {
  return [
  {
    rel: 'preload',
    as: 'style',
    href: '/style.css'
  },
  {
    rel: 'stylesheet',
    href: '/style.css'
  },
  {
    rel: 'icon',
    href: '/favicon.png'
  }];

};

export default function JobPage() {
  const data = useLoaderData<LoaderData>();
  const actionData = useActionData() as ActionData;
  const job = data.job;

  useSessionRewind({ enabled: data.includeSessionRewind });

  if (!job) {
    return null;
  }

  const isSuccess = actionData?.ok;

  return (
    <>
      <div className="relative overflow-hidden bg-white py-16">
        <div className="hidden lg:absolute lg:inset-y-0 lg:block lg:h-full lg:w-full">
          <div
            className="relative mx-auto h-full max-w-prose text-lg"
            aria-hidden="true">
            
            <svg
              className="absolute top-12 left-full translate-x-32 transform"
              width={404}
              height={384}
              fill="none"
              viewBox="0 0 404 384">
              
              <defs>
                <pattern
                  id="74b3fd99-0a6f-4271-bef2-e80eeafdf357"
                  x={0}
                  y={0}
                  width={20}
                  height={20}
                  patternUnits="userSpaceOnUse">
                  
                  <rect
                    x={0}
                    y={0}
                    width={4}
                    height={4}
                    className="text-gray-200"
                    fill="currentColor" />
                  
                </pattern>
              </defs>
              <rect
                width={404}
                height={384}
                fill="url(#74b3fd99-0a6f-4271-bef2-e80eeafdf357)" />
              
            </svg>
            <svg
              className="absolute top-1/2 right-full -translate-y-1/2 -translate-x-32 transform"
              width={404}
              height={384}
              fill="none"
              viewBox="0 0 404 384">
              
              <defs>
                <pattern
                  id="f210dbf6-a58d-4871-961e-36d5016a0f49"
                  x={0}
                  y={0}
                  width={20}
                  height={20}
                  patternUnits="userSpaceOnUse">
                  
                  <rect
                    x={0}
                    y={0}
                    width={4}
                    height={4}
                    className="text-gray-200"
                    fill="currentColor" />
                  
                </pattern>
              </defs>
              <rect
                width={404}
                height={384}
                fill="url(#f210dbf6-a58d-4871-961e-36d5016a0f49)" />
              
            </svg>
            <svg
              className="absolute bottom-12 left-full translate-x-32 transform"
              width={404}
              height={384}
              fill="none"
              viewBox="0 0 404 384">
              
              <defs>
                <pattern
                  id="d3eb07ae-5182-43e6-857d-35c643af9034"
                  x={0}
                  y={0}
                  width={20}
                  height={20}
                  patternUnits="userSpaceOnUse">
                  
                  <rect
                    x={0}
                    y={0}
                    width={4}
                    height={4}
                    className="text-gray-200"
                    fill="currentColor" />
                  
                </pattern>
              </defs>
              <rect
                width={404}
                height={384}
                fill="url(#d3eb07ae-5182-43e6-857d-35c643af9034)" />
              
            </svg>
          </div>
        </div>
        <div className="relative px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-prose text-lg">
            <div className="mb-12 flex justify-between">
              <Link
                to="/"
                className="flex items-center space-x-1 text-sm font-medium text-accent-color">
                
                <ChevronLeftIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                <span>Back</span>
              </Link>
              <Link
                to="apply"
                className="flex items-center space-x-1 self-end text-sm font-medium text-accent-color">
                
                <span>Apply here</span>
                <ChevronRightIcon className="ml-2 h-5 w-5" aria-hidden="true" />
              </Link>
            </div>
            <h1>
              <span className="block text-center text-base font-semibold uppercase tracking-wide text-accent-color">
                Full time
              </span>
              <span className="mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 sm:text-4xl">
                {job.name}
              </span>
            </h1>
            <p className="text-md mt-4 mb-8 flex items-center justify-center leading-8 text-gray-500">
              <span>{job.locations.join(', ')}</span>
              <svg
                className="mx-1.5 h-2 w-2 text-accent-color"
                fill="currentColor"
                viewBox="0 0 8 8">
                
                <circle cx={4} cy={4} r={3} />
              </svg>
              <span>{job.departments[0]}</span>
            </p>
            {/* <p className="mt-8 text-xl text-gray-500 leading-8">
              Aliquet nec orci mattis amet quisque ullamcorper neque, nibh sem. At arcu, sit dui mi, nibh dui, diam eget
              aliquam. Quisque id at vitae feugiat egestas ac. Diam nulla orci at in viverra scelerisque eget. Eleifend
              egestas fringilla sapien.
              </p> */}
          </div>
          <div
            className="prose prose-lg mx-auto mt-6 text-gray-500"
            dangerouslySetInnerHTML={{
              __html:
              job.ats === ATS.GREENHOUSE ?
              decodeHTML(job.description) :
              job.description
            }}>
          </div>
          {job.ats === ATS.LEVER &&
          (job.meta ? job.meta as any : {}).job?.lists?.length > 0 &&
          <div className="prose prose-lg mx-auto text-gray-500">
                {(job.meta ? job.meta as any : {}).job?.lists?.map(
              (list: any, index: number) =>
              <Fragment key={index}>
                      <h3 className="mt-8 mb-4 text-lg font-semibold text-gray-500">
                        {list.text}
                      </h3>
                      <ul
                  dangerouslySetInnerHTML={{ __html: list.content }}>
                </ul>
                    </Fragment>

            )}
              </div>
          }
          <div className="prose prose-lg relative mx-auto my-24">
            <div
              className="absolute inset-0 flex items-center"
              aria-hidden="true">
              
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-2 text-sm text-gray-500">Apply</span>
            </div>
          </div>
          <div className="prose prose-lg mx-auto">
            <div className="mb-24 flex justify-center">
              <Link
                to="apply"
                className="inline-flex justify-center rounded-md border border-transparent bg-accent-color py-3 px-8 text-sm font-medium text-white no-underline shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2">
                
                <span>Apply now</span>
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
              </Link>
            </div>
            {isSuccess &&
            <div>
                <span className="relative block text-center text-6xl">🙌</span>
                <p className="text-md text-center font-normal text-gray-600">
                  Thank you for submitting your application. We'll contact you
                  within 24 hours.
                </p>
              </div>
            }
            {/* {!isSuccess && <ApplicationForm job={job} />} */}
          </div>
        </div>
      </div>
      {/* <Example /> */}
    </>);

}